import { useCallback, useEffect, useMemo, useState } from "react";

import type { BuyerSubmissionStatus, BuyerSubmissionSummaryDto } from "@/api/buyers";
import {
  COMBINED_INTRO_MAX_LISTINGS,
  COMBINED_INTRO_MIN_LISTINGS,
  combinedIntroLeftOutReason,
} from "@/lib/buyerCombinedIntroRules";
import { listingNameOrFallback } from "@/lib/listingDisplay";

import { combinedIntroCopy } from "./combinedIntroCopy";

/**
 * The rows a specialist has ticked on ONE buyer's Submissions tab — for a
 * combined intro email, or for the bulk bar's ARCHIVE / DELETE (D3a).
 *
 * <p>🔴 <b>Every row is tickable (D3a, 2026-10-14).</b> Each bulk action applies
 * its own eligibility to the ticks: the combined intro leaves out a row whose
 * status (or archive) bars it and names it "left out" in the composer
 * ({@link CombinedIntroPick.leftOutReason}); Archive and Delete read the
 * server's per-row flags carried on the pick.</p>
 *
 * <p>🔴 <b>THIS SELECTION DELIBERATELY SURVIVES A PAGE, SORT OR FILTER CHANGE —
 * one of the two exceptions in this module to page-scoped selection</b> (the
 * other is Associated listings' PRESENT ALL, `associations/associationSelection.ts`).
 * The other multi-selects here (the buyers list, the queue) prune to the rows on screen,
 * because a bulk action there acts on what the reader can see. This one is
 * different in kind: the reader is ASSEMBLING an email, and the listings for it
 * are rarely on one page of a buyer who has been presented to thirty
 * businesses. Pruning on a page change would silently drop the listings they
 * picked two pages ago, and the email would go out short. So there is no
 * `pruneSelectionToVisible` here, and none must be added. What makes the
 * exception honest is that nothing is hidden: the bar says
 * "(on more than one page)" whenever the count includes rows the reader picked
 * elsewhere, and the composer lists every listing by number and name before
 * anything is drafted.</p>
 *
 * <p>Held in the TAB, keyed by submission id. It is cleared by Clear, by a
 * successful send or copy, and by leaving the buyer — a change of buyer id
 * resets it during render, and leaving the tab unmounts it.</p>
 */

/** What the table knew about a row when it was ticked. */
export interface CombinedIntroPick {
  submissionId: string;
  /** The listing's WHOLE canonical name — the composer lists these by number. */
  listingName: string;
  status: BuyerSubmissionStatus;
  introEmailSentAtUtc: string | null;
  /** Where the reader was when they ticked it: the ordering and page on screen.
   *  Two different values mean the selection was assembled on more than one
   *  page, which is what the bar tells them. */
  pickedOn: string;
  /** Why the combined intro leaves this pick out (a blocked status, or
   *  "archived"), or null when it can go in the email. */
  leftOutReason: string | null;
  // The server's own archive / delete answers for the row, carried so the bulk
  // bar can act on picks from other pages too.
  isArchived: boolean;
  canArchive: boolean;
  archiveBlockedReason: string | null;
  canDelete: boolean;
  deleteBlockedReason: string | null;
}

export interface CombinedIntroSelection {
  picks: CombinedIntroPick[];
  count: number;
  isSelected: (submissionId: string) => boolean;
  /** Ticks or unticks a row. Every row can be ticked (D3a). */
  toggle: (row: BuyerSubmissionSummaryDto, pickedOn: string) => void;
  clear: () => void;
  /** Drops these submissions from the selection — an archive or delete took
   *  them out of the default view (D4). */
  remove: (submissionIds: readonly string[]) => void;
  /** The picks the combined intro can include (no left-out reason). */
  introEligible: CombinedIntroPick[];
  /** True when the picks were made on more than one page or ordering. */
  spansPages: boolean;
  /** The sentence that says why Send is unavailable, or null when it is. */
  sendBlockedReason: string | null;
}

/** A pick built from the row as the table shows it now. */
export function pickFromRow(row: BuyerSubmissionSummaryDto, pickedOn: string): CombinedIntroPick {
  return {
    submissionId: row.id,
    listingName: listingNameOrFallback(row.listingDisplayName, row.listingBusinessName),
    status: row.status,
    introEmailSentAtUtc: row.introEmailSentAtUtc ?? null,
    pickedOn,
    leftOutReason: combinedIntroLeftOutReason(row),
    isArchived: row.isArchived ?? false,
    canArchive: row.canArchive ?? false,
    archiveBlockedReason: row.archiveBlockedReason ?? null,
    canDelete: row.canDelete ?? false,
    deleteBlockedReason: row.deleteBlockedReason ?? null,
  };
}

/**
 * Brings each pick's status, name and intro-sent date up to date from the rows
 * on screen, so a listing whose status moved after it was ticked is flagged
 * rather than carried to the server to be refused there.
 *
 * <p>🔴 <b>Returns `prev` UNCHANGED when nothing moved — the identity is the
 * contract.</b> This runs from an effect that sets state; a reducer that
 * allocates a new array every time never lets React bail out, and the render
 * loop that follows kills the test worker rather than failing an assertion
 * (see `buyerSelection.ts`). Its test asserts with `toBe` for that reason.</p>
 *
 * <p>It never ADDS or REMOVES a pick — only a reader does that. A pick whose
 * row is not on screen keeps what it had.</p>
 */
export function refreshPicks(
  prev: CombinedIntroPick[],
  rows: readonly BuyerSubmissionSummaryDto[],
): CombinedIntroPick[] {
  if (prev.length === 0 || rows.length === 0) return prev;

  const byId = new Map(rows.map((r) => [r.id, r]));
  let changed = false;
  const next = prev.map((p) => {
    const row = byId.get(p.submissionId);
    if (!row) return p;
    const fresh = pickFromRow(row, p.pickedOn);
    if (
      fresh.status === p.status &&
      fresh.listingName === p.listingName &&
      fresh.introEmailSentAtUtc === p.introEmailSentAtUtc &&
      fresh.leftOutReason === p.leftOutReason &&
      fresh.isArchived === p.isArchived &&
      fresh.canArchive === p.canArchive &&
      fresh.archiveBlockedReason === p.archiveBlockedReason &&
      fresh.canDelete === p.canDelete &&
      fresh.deleteBlockedReason === p.deleteBlockedReason
    )
      return p;
    changed = true;
    return fresh;
  });
  return changed ? next : prev;
}

/** The picks the combined intro can include — ineligible ones are left out
 *  (named in the composer), never a reason to refuse the whole email (D3a). */
export function introEligiblePicks(picks: readonly CombinedIntroPick[]): CombinedIntroPick[] {
  return picks.filter((p) => !p.leftOutReason);
}

/**
 * Why Send is unavailable for these picks, or null when it is available. The
 * min / max rules apply to the ELIGIBLE count (D3a): left-out picks neither
 * count toward the two nor block the email.
 */
export function sendBlockedReasonFor(picks: readonly CombinedIntroPick[]): string | null {
  const eligible = introEligiblePicks(picks).length;
  if (eligible < COMBINED_INTRO_MIN_LISTINGS) return combinedIntroCopy.needTwo;
  if (eligible > COMBINED_INTRO_MAX_LISTINGS) return combinedIntroCopy.tooMany;
  return null;
}

export function useCombinedIntroSelection(
  buyerLeadId: string,
  visibleRows: readonly BuyerSubmissionSummaryDto[],
): CombinedIntroSelection {
  const [picks, setPicks] = useState<CombinedIntroPick[]>([]);

  // Leaving the buyer clears it. Derived during render, as `useRecordSubmissions`
  // resets its page: an effect would paint the old buyer's count first.
  const [forBuyer, setForBuyer] = useState(buyerLeadId);
  if (forBuyer !== buyerLeadId) {
    setForBuyer(buyerLeadId);
    setPicks([]);
  }

  // 🔴 `visibleRows` must be referentially stable across renders with the same
  // data (`useRecordSubmissions` memoises it); `refreshPicks` returns `prev`
  // when nothing moved, so this settles.
  useEffect(() => {
    setPicks((prev) => refreshPicks(prev, visibleRows));
  }, [visibleRows]);

  const selectedIds = useMemo(() => new Set(picks.map((p) => p.submissionId)), [picks]);

  const toggle = useCallback((row: BuyerSubmissionSummaryDto, pickedOn: string) => {
    setPicks((prev) => {
      if (prev.some((p) => p.submissionId === row.id))
        return prev.filter((p) => p.submissionId !== row.id);
      return [...prev, pickFromRow(row, pickedOn)];
    });
  }, []);

  const clear = useCallback(() => setPicks([]), []);

  const remove = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return;
    const gone = new Set(ids);
    setPicks((prev) => (prev.some((p) => gone.has(p.submissionId))
      ? prev.filter((p) => !gone.has(p.submissionId))
      : prev));
  }, []);

  return {
    picks,
    count: picks.length,
    isSelected: (id) => selectedIds.has(id),
    toggle,
    clear,
    remove,
    introEligible: introEligiblePicks(picks),
    spansPages: new Set(picks.map((p) => p.pickedOn)).size > 1,
    sendBlockedReason: sendBlockedReasonFor(picks),
  };
}
